import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { package as packagePropType } from 'lib/CustomPropTypes';
import Breakpoints from 'lib/Breakpoints';
import { getItemMetadataValue } from 'lib/packageHelper';
import { getPlaymakerMetadata } from 'lib/playmaker';

import PackageTitle from 'components/PackageTitle';
import { PlaymakerTease } from 'components/PlaymakerTease';
import SlideshowTeasePicture from 'components/SlideshowTeasePicture';
import TeaseCard from 'components/TeaseCard';
import TeasePicture from 'components/TeasePicture';

import { getUpImageSize } from './imageConfig';

const block = 'dynamic-up';

function DynamicUp(props) {
  const { package: pkg } = props;
  if (!pkg || !(pkg.items && pkg.items[0])) {
    return null;
  }

  const {
    id,
    items,
    metadata: packageMetadata = {},
  } = pkg;
  const {
    pkgClassName,
    railAdjacent,
    vertical,
  } = props;

  // playmakerToggle on by default
  if (typeof packageMetadata.playmakerToggle === 'undefined') {
    packageMetadata.playmakerToggle = true;
  }

  // get playmaker configuration
  const playmakerConfig = getPlaymakerMetadata(packageMetadata, vertical);

  const [isMobile, setIsMobile] = React.useState(true);

  // useEffect to toggle display of playmaker tease on non-mobile
  // 'id' prop should not change, safe to rely on this running once
  React.useEffect(() => {
    if (!Breakpoints.isS()) {
      setIsMobile(false);
    }
  }, [id]);

  const filteredItems = items.filter((item) => !!item && item.id);
  let itemCount = filteredItems.length;

  let hasPlaymaker = false;
  if (!isMobile && playmakerConfig.playmakerToggle) {
    // Toggle playmaker
    hasPlaymaker = true;
    // Increment count
    itemCount += 1;
  }

  // Get configured metadata value
  const getItemMetadata = getItemMetadataValue(packageMetadata);

  return (
    <section
      className={classNames(
        block,
        pkgClassName,
        {
          [`${block}__${itemCount}up--full`]: !railAdjacent,
          [`${block}__${itemCount}up--rail-adj`]: railAdjacent,
        },
      )}
      data-packageid={id}
      data-test="dynamic-up__section"
    >
      <PackageTitle metadata={packageMetadata} />
      <div className={`${block}__tease-cards`}>
        {hasPlaymaker && (
          <article className="tease-card">
            <PlaymakerTease
              icid="tease-dynamicup"
              metadata={playmakerConfig}
            />
          </article>
        )}
        {filteredItems.map((item) => {
          const { type } = item;
          const teaseSizes = getUpImageSize({
            count: itemCount,
            railAdj: railAdjacent,
            type,
            videoAdj: hasPlaymaker,
          });
          return (
            <TeaseCard
              content={item}
              isRailAdjacent={railAdjacent}
              key={item.id}
              packageId={id}
              showAuthor={getItemMetadata('showAuthor', item.metadata)}
              showDek={getItemMetadata('showDek', item.metadata)}
              showFeaturedAuthor={getItemMetadata('showFeaturedAuthor', item.metadata)}
              showTeaseImage={getItemMetadata('showTeaseImage', item.metadata)}
              showTimestamp={getItemMetadata('showTimestamp', item.metadata)}
              vertical={vertical}
            >
              {type === 'slideshow' ? (
                <SlideshowTeasePicture
                  sizes={teaseSizes}
                  slides={item.item.slides}
                />
              ) : (
                <TeasePicture
                  computedValues={item.computedValues}
                  responsiveFlavors={teaseSizes}
                  type={item.type}
                />
              )}
            </TeaseCard>
          );
        })}
      </div>
    </section>
  );
}

DynamicUp.propTypes = {
  package: packagePropType.isRequired,
  pkgClassName: PropTypes.string,
  railAdjacent: PropTypes.bool,
  vertical: PropTypes.string,
};

DynamicUp.defaultProps = {
  pkgClassName: 'pkg',
  railAdjacent: false,
  vertical: null,
};

export { DynamicUp };
